import React, { Suspense } from "react";
import { Navigate, Outlet } from "react-router";
import type { RouteObject } from "react-router";
import {
    faTachometerAlt,
    faListOl,
    faUserCheck,
    faChartSimple,
    faBuilding,
} from "@fortawesome/free-solid-svg-icons";
import { faFile } from "@fortawesome/free-regular-svg-icons";
import NotFound from "../../shared/components/NotFound";
import LoadingScreen from "../../shared/components/Loading";
import { lazyWithRetry } from "../../shared/components/ChunkErrorBoundary";
import Layout from "../../shared/components/redesigned/Layout";
import type { SidebarItem } from "../../shared/components/redesigned/Sidebar";
import useTranslation from "../../shared/hooks/useTranslation";
import RequireRole, { SUPERVISOR_ROLES } from "../auth/components/RequireRole";
import UnitsScreen from "./routes/Units";
import UnitShow from "./routes/Units/components/UnitShow";

const QueuedAccountsScreen = lazyWithRetry(() => import("./routes/QueuedAccountsScreen"));
const AssignedAccountsScreen = lazyWithRetry(() => import("./routes/AssignedAccountsScreen"));
const ProcessingStatsScreen = lazyWithRetry(() => import("./routes/ProcessingStatsScreen"));
const AccountsScreen = lazyWithRetry(() => import("./routes/AccountsScreen"));
const ProcessingPerformanceScreen = lazyWithRetry(
    () => import("./routes/ProcessingPerformanceScreen"),
);

function SupervisorLayout() {
    const { t } = useTranslation();
    const navItems: SidebarItem[] = [
        {
            to: "/supervising/queued",
            icon: faListOl,
            label: t("nav.supervising.queue"),
        },
        {
            to: "/supervising/assigned",
            icon: faUserCheck,
            label: t("nav.supervising.processing"),
        },
        {
            to: "/supervising/units",
            icon: faFile,
            label: t("nav.supervising.units"),
        },
        {
            to: "/supervising/accounts",
            icon: faBuilding,
            label: t("nav.supervising.accounts"),
        },
        {
            to: "/supervising/processing-stats",
            icon: faChartSimple,
            label: t("nav.supervising.processing_stats"),
        },
        {
            to: "/supervising/processing-performance",
            icon: faTachometerAlt,
            label: t("nav.supervising.processing_performance"),
        },
    ];

    return (
        <Layout navItems={navItems}>
            <Suspense fallback={<LoadingScreen />}>
                <Outlet />
            </Suspense>
        </Layout>
    );
}

export const supervisingRoutes: RouteObject[] = [
    {
        element: <RequireRole roles={SUPERVISOR_ROLES} />,
        children: [
            {
                element: <SupervisorLayout />,
                children: [
                    { index: true, element: <Navigate to="queued" replace /> },
                    { path: "queued", element: <QueuedAccountsScreen /> },
                    { path: "assigned", element: <AssignedAccountsScreen /> },
                    { path: "processing-stats", element: <ProcessingStatsScreen /> },
                    { path: "processing-performance", element: <ProcessingPerformanceScreen /> },
                    {
                        path: "units",
                        element: <UnitsScreen />,
                        children: [{ path: ":unitId", element: <UnitShow /> }],
                    },
                    { path: "accounts", element: <AccountsScreen /> },
                    { path: "*", element: <NotFound /> },
                ],
            },
        ],
    },
];
